/* GrowthOS re-skin note: this hook was previously dead code (nothing imported it —
   campaigns.jsx built its columnDefs inline via a `useState`+`useEffect`). It is now
   the live source of the Campaigns grid columns, matching the pattern already used by
   useDealColumnDefs.js / useLeadColumnDefs.js.

   The real field set below is taken verbatim from the ground-truth `useEffect` that
   used to live in campaigns.jsx (campaignName / campaignType / scheduledDateTime /
   templateName / campaignStatus / createdBy / updatedBy / createdDateTime(hide) /
   updatedDateTime(hide) / tagIds(hide) / description(hide) / owner) — only the cell
   presentation changed, not the field/filter set:
     - Checkbox column is now field-less, pinned-left, 48px, no header label (matches
       leads/contacts/accounts/deals).
     - Campaign Name folds Template Name in as a muted subtitle (design's "cell-lead"
       treatment) via a color-chip icon box, keyed off campaignType via a deterministic
       hash → palette (same AVA_COLORS approach as DealsChangeOwnerModal.jsx).
     - Template Name and Campaign Owner stay in columnDefs (still real, filterable,
       exportable AG columns — native header filters still work) but are `hide: true`
       by default, matching the design's exact 6-column grid (Name/Type/Schedule/
       Status/Created/Updated only) and excluded from the custom Columns-menu list
       built in campaigns.jsx.
     - Status renders as a small tinted pill with a leading dot (`.gos-badge`/
       `.gos-b-*`/`.gos-pip` — the same generic badge classes already used by the
       reskinned opportunities tab widget), tone derived from the status string via
       case-insensitive substring matching (admin-configurable master data, so no
       fixed enum).
     - Created By / Updated By render as avatar + name (`.gos-owner-cell`/`.gos-ava.sm`/
       `.gos-owner-name` — the same classes useLeadColumnDefs.js already uses for its
       Owner cell) instead of the old plain-text NameFormatter valueFormatter.
     - No per-row actions column: like leads/contacts/accounts/deals, the grid is
       checkbox-only — view via the Campaign Name link, edit/delete via bulk selection.
       (An earlier pass added a pinned-right dots menu; it was removed to stay
       consistent with the other four list modules.)
     - `owner` and `campaignType` colDefs now explicitly carry `type: 'dropdown'` (the
       live file was missing this) so campaigns.jsx's mass-update field enrichment can
       detect them as dropdown-type fields, mirroring Deals' stage/projectType/etc. */
import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { getCommonTextFilterParams, getSetFilterParams, getSetDatefilterParams } from '../../utils/agGridFilters';
import { isMobile } from '../../utils/deviceUtils';
import { DateTimeFormatter, NameFormatter } from '../../shared/controls/formatters';

// same deterministic hash → palette approach as DealsChangeOwnerModal.jsx, reused
// here for the Campaign Name icon chip (keyed off campaignType) and the Created
// By / Updated By avatars, so colors read consistently across the app.
const AVA_COLORS = ['#c96342', '#2e63a6', '#2c7a45', '#9a751c', '#6a4fae', '#b34480', '#54738f'];
const initials = (name) =>
  String(name || '')
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0] || '')
    .join('')
    .toUpperCase() || '?';
const avaColor = (name) =>
  AVA_COLORS[
    String(name || '')
      .split('')
      .reduce((a, c) => a + c.charCodeAt(0), 0) % AVA_COLORS.length
  ];

// tone derived from the status string via case-insensitive substring matching —
// campaignStatus is admin-configurable master data, not a fixed enum. "inactive"
// is checked before "active" since it's a superset match.
const statusTone = (label) => {
  const s = String(label || '').toLowerCase();
  if (s.includes('inactive')) return 'red';
  if (s.includes('active')) return 'green';
  if (s.includes('complet')) return 'gray';
  if (s.includes('schedul')) return 'blue';
  if (s.includes('pause')) return 'amber';
  return 'gray';
};

// Campaign-type → icon + color, mirroring the design's TYPE_ICON / TYPE_COLOR maps
// (growthos/CampaignsList.jsx) so each type renders a distinct glyph in the name-cell
// chip. Because real campaignType master data is admin-configurable (it won't always
// match the design's exact strings), resolveType() tries: (1) the design's exact
// names, (2) keyword matching for common variants, (3) a deterministic hash → glyph,
// guaranteeing consistent per-type variety for any value. `spark` is a filled glyph.
const TYPE_GLYPHS = {
  mail: { d: 'M4 4h16a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2ZM22 7l-10 6L2 7' },
  note: { d: 'M14 3v5h5M14 3H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-5ZM8 13h8M8 17h5' },
  target: { d: 'M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20ZM12 16a4 4 0 1 0 0-8 4 4 0 0 0 0 8ZM12 12h.01' },
  building: { d: 'M3 21h18M5 21V5a1 1 0 0 1 1-1h6a1 1 0 0 1 1 1v16M13 9h5a1 1 0 0 1 1 1v11M8 8h2M8 12h2M8 16h2M16 13h.01M16 17h.01' },
  refresh: { d: 'M21 12a9 9 0 1 1-3-6.7L21 8M21 3v5h-5' },
  campaigns: { d: 'M3 11v2a1 1 0 0 0 1 1h2l4 4V6L6 10H4a1 1 0 0 0-1 1ZM15 8a4 4 0 0 1 0 8M18 5a8 8 0 0 1 0 14' },
  zap: { d: 'M13 2 4 14h7l-1 8 9-12h-7l1-8Z' },
  calendar: { d: 'M8 2v4M16 2v4M3 9h18M5 4h14a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2Z' },
  globe: { d: 'M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20ZM2 12h20M12 2a15 15 0 0 1 0 20 15 15 0 0 1 0-20Z' },
  spark: { d: 'M12 3l2.4 6.6L21 12l-6.6 2.4L12 21l-2.4-6.6L3 12l6.6-2.4L12 3Z', filled: true },
};
// design's exact type-name → [glyph key, color]
const TYPE_MAP = {
  'outbound sequence': ['mail', 'var(--gos-accent)'],
  'email nurture': ['mail', '#2e63a6'],
  'content nurture': ['note', '#9a751c'],
  retargeting: ['target', '#b34480'],
  'account-based': ['building', '#6a4fae'],
  lifecycle: ['refresh', '#2c7a45'],
  broadcast: ['campaigns', '#c0473f'],
  'multi-step': ['zap', '#54738f'],
  event: ['calendar', '#0e8c6f'],
  're-engagement': ['spark', '#9d4a2c'],
};
// keyword → glyph key, for admin-configured type names outside the design's set
const TYPE_KEYWORDS = [
  [/mail|email|drip|newsletter/, 'mail'],
  [/event|webinar|conference|meetup|summit/, 'calendar'],
  [/retarget|\bads?\b|ppc|display/, 'target'],
  [/account|abm/, 'building'],
  [/lifecycle|onboard|renewal|trial|welcome/, 'refresh'],
  [/broadcast|announce|launch|blast/, 'campaigns'],
  [/nurture|content|whitepaper|ebook|blog/, 'note'],
  [/re-?engage|winback|win-back|revival|dormant|reactivat/, 'spark'],
  [/multi|sequence|step|workflow|journey/, 'zap'],
  [/social|web|online/, 'globe'],
];
const GLYPH_KEYS = Object.keys(TYPE_GLYPHS);
const resolveType = (type) => {
  const key = String(type || '').trim().toLowerCase();
  if (TYPE_MAP[key]) return { glyph: TYPE_GLYPHS[TYPE_MAP[key][0]], color: TYPE_MAP[key][1] };
  const kw = TYPE_KEYWORDS.find(([re]) => re.test(key));
  const glyphKey = kw ? kw[1] : GLYPH_KEYS[key.split('').reduce((a, c) => a + c.charCodeAt(0), 0) % GLYPH_KEYS.length];
  return { glyph: TYPE_GLYPHS[glyphKey], color: avaColor(key) };
};

const useCampaignColumnDefs = ({
  tagsList,
  owner,
  gridData,
  campaignType,
  campaignStatus,
}) => {
  const commonTextFilterParams = useMemo(() => getCommonTextFilterParams(), []);
  const dateFilterParams = useMemo(() => getSetDatefilterParams(), []);

  const actionsColumn = useMemo(
    () => [
      {
        headerName: '',
        headerCheckboxSelection: (params) => params?.node && !params.node.group,
        checkboxSelection: (params) => params?.node && !params.node.group,
        headerCheckboxSelectionFilteredOnly: true,
        maxWidth: 48,
        minWidth: 48,
        sortable: false,
        filter: false,
        resizable: false,
        pinned: 'left',
      },
    ],
    []
  );

  const columnDefs = useMemo(() => {
    if (!campaignType?.length || !campaignStatus?.length || !owner?.length) return [];

    return [
      ...actionsColumn,
      {
        headerName: 'Campaign Name',
        field: 'campaignName',
        type: 'text',
        pinned: isMobile() ? null : 'left',
        minWidth: 240,
        cellRenderer: (params) => {
          if (!params.value || params.node?.group) return params.value;
          const { glyph, color } = resolveType(params.data?.campaignType);
          return (
            <div className="gos-lead-cell">
              <span
                className="gos-ava"
                style={{ width: 36, height: 36, borderRadius: 9, background: color + '22', color }}
              >
                <svg viewBox="0 0 24 24" width="16" height="16" fill={glyph.filled ? 'currentColor' : 'none'} stroke={glyph.filled ? 'none' : 'currentColor'} strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
                  <path d={glyph.d} />
                </svg>
              </span>
              <div className="gos-lead-cell-main">
                <Link
                  to={`/campaigns/viewCampaign?id=${params?.data?.id}&pageNo=${gridData?.inputPayload?.pageNumber}`}
                  state={params.data}
                  className="gos-lead-name"
                >
                  {params.value}
                </Link>
                {params.data?.templateName ? <span className="gos-lead-sub">{params.data.templateName}</span> : null}
              </div>
            </div>
          );
        },
        filter: 'agTextColumnFilter',
        filterParams: commonTextFilterParams,
      },
      {
        headerName: 'Type',
        field: 'campaignType',
        type: 'dropdown',
        filter: 'agSetColumnFilter',
        filterParams: getSetFilterParams(campaignType?.map((item) => item.displayName)),
      },
      {
        headerName: 'Schedule At',
        field: 'scheduledDateTime',
        type: 'date',
        filter: 'agDateColumnFilter',
        filterParams: dateFilterParams,
        minWidth: 200,
        valueFormatter: DateTimeFormatter,
      },
      {
        headerName: 'Template Name',
        field: 'templateName',
        type: 'dropdown',
        filter: 'agTextColumnFilter',
        filterParams: commonTextFilterParams,
        // folded into the Campaign Name cell as a subtitle (design's "cell-lead"
        // treatment) — still a real, filterable/exportable column, just hidden by
        // default and excluded from the Columns menu (see campaigns.jsx).
        hide: true,
        suppressColumnsToolPanel: true,
      },
      {
        headerName: 'Status',
        field: 'campaignStatus',
        type: 'dropdown',
        filter: 'agSetColumnFilter',
        filterParams: getSetFilterParams(campaignStatus?.map((item) => item.displayName)),
        cellRenderer: (params) => {
          if (!params.value) return null;
          const tone = statusTone(params.value);
          return (
            <span className={'gos-badge gos-b-' + tone}>
              <span className="gos-pip" />
              {params.value}
            </span>
          );
        },
      },
      {
        headerName: 'Created By',
        field: 'createdBy',
        minWidth: 200,
        filter: 'agTextColumnFilter',
        filterParams: commonTextFilterParams,
        suppressAndOrCondition: true,
        cellRenderer: (params) => {
          const name = NameFormatter(params.value);
          if (!name) return null;
          return (
            <div className="gos-owner-cell">
              <span className="gos-ava sm" style={{ background: avaColor(name) }}>
                {initials(name)}
              </span>
              <span className="gos-owner-name">{name}</span>
            </div>
          );
        },
      },
      {
        headerName: 'Updated By',
        field: 'updatedBy',
        minWidth: 200,
        filter: 'agTextColumnFilter',
        filterParams: commonTextFilterParams,
        suppressAndOrCondition: true,
        cellRenderer: (params) => {
          const name = NameFormatter(params.value);
          if (!name) return null;
          return (
            <div className="gos-owner-cell">
              <span className="gos-ava sm" style={{ background: avaColor(name) }}>
                {initials(name)}
              </span>
              <span className="gos-owner-name">{name}</span>
            </div>
          );
        },
      },
      {
        headerName: 'Created Date Time(UTC)',
        field: 'createdDateTime',
        minWidth: 230,
        filter: 'agDateColumnFilter',
        filterParams: dateFilterParams,
        valueFormatter: DateTimeFormatter,
        hide: true,
      },
      {
        headerName: 'Updated Date Time(UTC)',
        field: 'updatedDateTime',
        minWidth: 230,
        filter: 'agDateColumnFilter',
        filterParams: dateFilterParams,
        valueFormatter: DateTimeFormatter,
        hide: true,
      },
      {
        headerName: 'Tags',
        field: 'tagIds',
        valueGetter: (params) => params?.data?.tags,
        filter: 'agSetColumnFilter',
        filterParams: getSetFilterParams(tagsList?.map((item) => item?.name[0])),
        hide: true,
      },
      {
        headerName: 'Description',
        field: 'description',
        type: 'text',
        filter: 'agTextColumnFilter',
        filterParams: commonTextFilterParams,
        hide: true,
      },
      {
        headerName: 'Campaign Owner',
        field: 'owner',
        type: 'dropdown',
        filter: 'agSetColumnFilter',
        filterParams: getSetFilterParams(owner?.map((item) => item.display_name)),
        // real filterable/mass-updatable field, but the design has no separate
        // "Campaign Owner" grid column — hidden by default, excluded from the
        // Columns menu (see campaigns.jsx), same treatment as Template Name.
        hide: true,
        suppressColumnsToolPanel: true,
      },
    ];
  }, [
    campaignType,
    campaignStatus,
    owner,
    tagsList,
    gridData,
    actionsColumn,
    commonTextFilterParams,
    dateFilterParams,
  ]);

  return columnDefs;
};

export default useCampaignColumnDefs;
